/**
 * @file    serializerDelta.ts
 * @scope   SHARED
 * @summary CPR serializer delta on top of the engine core (Template Method, plan §7.2).
 * @consumes SerializerDelta, CprSlice, RollupMetricModel, CPR constants
 * @produces CPR_SERIALIZER_DELTA
 * @sideEffects None (the core performs the config mutation).
 * @notes   Persisted shape = `GuardRailsConfig` (docs/guardrails.md, "Cross-Program Rollup"):
 *          - widget level (siblings of `Chart`): `AggregationType`, `Trend` (1-item array),
 *            `CustomCalculations` (the rollup metric's calculations, applied to ALL programs);
 *          - `QueryCollections`: the rollup metric's programs as symmetric peers. The core writes
 *            `DataCollectionGuid` + `OriginId` and this delta adds NOTHING else (no WorkspaceGuid,
 *            no Fields/Trend/AggregationType per collection — documented anti-pattern).
 *          Only `metrics[0]` is persisted (`CPR_MAX_ROLLUP_METRICS` = 1).
 *
 * Called from the drawer by `useEnginePersistence(activeDescriptor)` via `serializeWithDelta`
 * (no `trend` option: CPR's trend is widget-level and comes from the slice).
 * TODO(ai): the metric `displayName` has no backend field yet, so it is not persisted and is
 * rebuilt with the default name on load. Persist it once GuardRailsConfig exposes a field.
 */
import type { SerializerDelta } from '../../engine/contracts';
import {
  CPR_AGGREGATION_TYPES,
  CPR_CHART_TYPE,
  CPR_PAYLOAD_VERSION,
  CPR_TREND_PERIOD_TYPES,
  toCprEnum,
} from './constants';
import { CPR_INITIAL_STATE } from './state/cprReducer';
import { CprAggregationType, CprCalculation, CprContributor, CprSlice, CprTrend } from './model';
import { RollupMetricModel, createRollupMetric } from 'models/CrossProgramRollup/RollupMetricModel';
import { createRollupProgramId } from 'models/CrossProgramRollup/RollupProgramModel';

/** The single persisted rollup metric (see `CPR_MAX_ROLLUP_METRICS`). */
const persistedMetric = (slice: CprSlice): RollupMetricModel | undefined => slice.metrics[0];

/** WHY: `Unknown`/unrecognised values are dropped so the save guard flags them (fail loud). */
const readAggregationType = (value: unknown): CprAggregationType | undefined =>
  toCprEnum(value, CPR_AGGREGATION_TYPES);

/** Reads the first trend of the widget-level `trend` array; rejects `Unknown` periods. */
const readTrend = (value: unknown): CprTrend | undefined => {
  const trend = Array.isArray(value) ? value[0] : undefined;
  if (!trend || typeof trend !== 'object') return undefined;
  const timePeriodType = toCprEnum((trend as CprTrend).timePeriodType, CPR_TREND_PERIOD_TYPES);
  return timePeriodType ? { ...(trend as CprTrend), timePeriodType } : undefined;
};

const readCalculations = (value: unknown): CprCalculation[] =>
  Array.isArray(value)
    ? value.filter((c): c is CprCalculation => !!c && typeof c === 'object')
    : [];

/** Rebuilds the rollup metric from persisted pieces; none when nothing was authored. */
const toMetrics = (
  programs: CprContributor[],
  calculations: CprCalculation[]
): RollupMetricModel[] =>
  programs.length || calculations.length
    ? [{ ...createRollupMetric(1), programs, calculations }]
    : [];

export const CPR_SERIALIZER_DELTA: SerializerDelta<CprSlice> = {
  version: CPR_PAYLOAD_VERSION,

  getCollections: (slice) =>
    (persistedMetric(slice)?.programs ?? []).map((p) => ({
      guid: p.dataCollectionGuid,
      originId: p.originId,
    })),

  // WHY empty: contributors are symmetric peers carrying only their identity.
  serializeCollection: () => ({}),

  deserializeCollections: (collections) => ({
    ...CPR_INITIAL_STATE,
    metrics: toMetrics(
      collections.map((qc) => ({
        id: createRollupProgramId(),
        dataCollectionGuid: qc.dataCollectionGuid ?? '',
        originId: qc.originId ?? '',
      })),
      []
    ),
  }),

  emptySlice: () => CPR_INITIAL_STATE,

  serializeWidget: (slice) => ({
    chart: CPR_CHART_TYPE,
    aggregationType: slice.aggregationType,
    trend: slice.trend ? [slice.trend] : [],
    customCalculations: persistedMetric(slice)?.calculations ?? [],
  }),

  deserializeWidget: (config, slice) => ({
    ...slice,
    aggregationType: readAggregationType(config?.aggregationType),
    trend: readTrend(config?.trend),
    metrics: toMetrics(
      slice.metrics[0]?.programs ?? [],
      readCalculations(config?.customCalculations)
    ),
  }),

  migrate: (config) => config,
};
